// ========= Copyright 2025-2026 @ Eigent.ai All Rights Reserved. =========
// Licensed under the Apache License, Version 2.0 (the "License");
// you may not use this file except in compliance with the License.
// You may obtain a copy of the License at
//
//     http://www.apache.org/licenses/LICENSE-2.0
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
// See the License for the specific language governing permissions and
// limitations under the License.
// ========= Copyright 2025-2026 @ Eigent.ai All Rights Reserved. =========

/**
 * Hanggent's product pages, one row each. The routes, the sidebar group and
 * the launcher page are all generated from this table; to add a product, add a
 * row here and nothing else. In the community edition no product is visible.
 */

import { isDailyStockAnalysisAvailable } from '@/lib/daily-stock-analysis';
import { features, type ServiceKey } from '@/lib/edition';
import {
  BrainCircuit,
  Building2,
  Compass,
  GraduationCap,
  LayoutDashboard,
  LineChart,
  SearchCheck,
  Sparkles,
  TrendingUp,
  type LucideIcon,
} from 'lucide-react';
import type { ComponentType } from 'react';

export type ProductDomain =
  | 'stock'
  | 'career'
  | 'car'
  | 'real-estate'
  | 'education'
  | 'media'
  | 'agents';

export interface ProductContext {
  email: string | null;
}

export interface ProductDefinition {
  id: string;
  /** Under /products/: the ingress sends /comfyui, /vibe-astock, ... to the
   * services themselves, so a page at those paths would not survive a reload. */
  path: string;
  /** The address the old app used; it redirects to `path`. */
  legacyPath?: string;
  labelKey: string;
  defaultLabel: string;
  descriptionKey: string;
  defaultDescription: string;
  icon: LucideIcon;
  domain: ProductDomain;
  /** The operator can switch this service off (see lib/edition). */
  service?: ServiceKey;
  /** Reachable by address only: not listed in the launcher or the rail. */
  hidden?: boolean;
  /** Extra condition: an address is configured, the user is an admin, ... */
  isAvailable?: (ctx: ProductContext) => boolean;
  load: () => Promise<{ default: ComponentType }>;
}

export const PRODUCTS: ProductDefinition[] = [
  {
    id: 'stock-suite',
    path: '/products/stock-suite',
    legacyPath: '/stock-suite',
    labelKey: 'layout.stock-suite',
    defaultLabel: 'Stock Suite',
    descriptionKey: 'products.stock-suite-description',
    defaultDescription:
      'Stock intelligence projects in one place: screening, research and daily analysis.',
    icon: LayoutDashboard,
    domain: 'stock',
    load: () => import('@/pages/StockSuite'),
  },
  {
    id: 'research-center',
    path: '/products/research-center',
    legacyPath: '/research-center',
    labelKey: 'layout.research-center',
    defaultLabel: 'Research Center',
    descriptionKey: 'products.research-center-description',
    defaultDescription:
      'Screening, research runs, portfolio and daily analysis in one place.',
    icon: LayoutDashboard,
    domain: 'stock',
    load: () => import('@/pages/ResearchCenter'),
  },
  {
    id: 'alphasift',
    path: '/products/alphasift',
    legacyPath: '/alphasift',
    labelKey: 'products.alphasift',
    defaultLabel: 'AlphaSift',
    descriptionKey: 'products.alphasift-description',
    defaultDescription:
      'Screen the market with a strategy and evaluate the run.',
    icon: Sparkles,
    domain: 'stock',
    load: () => import('@/pages/stock/AlphaSiftPage'),
  },
  {
    id: 'vibe-research',
    path: '/products/vibe-research',
    legacyPath: '/vibe-research',
    labelKey: 'stockProducts.suite.researchName',
    defaultLabel: 'Vibe Research',
    descriptionKey: 'products.vibe-research-description',
    defaultDescription: 'Research a company with an AI analyst.',
    icon: SearchCheck,
    domain: 'stock',
    service: 'vibeResearch',
    load: () => import('@/pages/VibeResearch'),
  },
  {
    id: 'vibe-astock',
    path: '/products/vibe-astock',
    legacyPath: '/vibe-astock',
    labelKey: 'stockProducts.suite.astockName',
    defaultLabel: 'Vibe AStock',
    descriptionKey: 'products.vibe-astock-description',
    defaultDescription: 'A-share analysis and screening.',
    icon: TrendingUp,
    domain: 'stock',
    service: 'vibeAstock',
    load: () => import('@/pages/VibeAstock'),
  },
  {
    id: 'career-recommendations',
    path: '/products/career-recommendations',
    labelKey: 'products.career-recommendations',
    defaultLabel: 'Career Recommendations',
    descriptionKey: 'products.career-recommendations-description',
    defaultDescription:
      'Find United States and Canadian roles matched to your resume.',
    icon: Compass,
    domain: 'career',
    load: () => import('@/pages/CareerRecommendations'),
  },
  {
    id: 'zhitu-career',
    path: '/products/zhitu-career',
    labelKey: 'products.zhitu-career',
    defaultLabel: 'Zhitu Career',
    descriptionKey: 'products.zhitu-career-description',
    defaultDescription: 'Search jobs and match them against your resume.',
    icon: Compass,
    domain: 'career',
    load: () => import('@/pages/ZhituCareer'),
  },
  {
    id: 'car-search',
    path: '/products/car-search',
    legacyPath: '/car-search',
    labelKey: 'layout.car-search',
    defaultLabel: 'Used Cars',
    descriptionKey: 'products.car-search-description',
    defaultDescription:
      'Watch used-car listings, compare prices and get alerts on changes.',
    icon: Compass,
    domain: 'car',
    load: () => import('@/pages/CarSearchWorkspace'),
  },
  {
    id: 'daily-stock-analysis',
    path: '/products/daily-stock-analysis',
    legacyPath: '/daily-stock-analysis',
    labelKey: 'layout.daily-stock-analysis',
    defaultLabel: 'Daily Stock Analysis',
    descriptionKey: 'products.daily-stock-analysis-description',
    defaultDescription: 'A daily written analysis of the stocks you follow.',
    icon: LineChart,
    domain: 'stock',
    isAvailable: () => isDailyStockAnalysisAvailable(),
    load: () => import('@/pages/DailyStockAnalysis'),
  },
  {
    id: 'real-estate',
    path: '/products/real-estate',
    legacyPath: '/real-estate',
    labelKey: 'layout.real-estate',
    defaultLabel: 'Real Estate',
    descriptionKey: 'products.real-estate-description',
    defaultDescription:
      'Track listings, compare properties and review daily changes.',
    icon: Building2,
    domain: 'real-estate',
    load: () => import('@/pages/RealEstateAssistant'),
  },
  {
    id: 'education-center',
    path: '/products/education-center',
    legacyPath: '/education-center',
    labelKey: 'layout.education-center',
    defaultLabel: 'Education Center',
    descriptionKey: 'products.education-center-description',
    defaultDescription: 'Plan studies and track official admissions updates.',
    icon: GraduationCap,
    domain: 'education',
    load: () => import('@/pages/EducationCenter'),
  },
  {
    id: 'private-model-studio',
    path: '/products/private-model-studio',
    legacyPath: '/private-model-studio',
    labelKey: 'privateModel.title',
    defaultLabel: 'Private Model Studio',
    descriptionKey: 'privateModel.intro',
    defaultDescription: 'Review company materials and train a local model.',
    icon: BrainCircuit,
    domain: 'agents',
    load: () => import('@/pages/PrivateModelStudio'),
  },
  {
    id: 'stock-analysis-legacy',
    path: '/stock-analysis',
    labelKey: 'layout.stock-suite',
    defaultLabel: 'Stock Suite',
    descriptionKey: 'products.stock-suite-description',
    defaultDescription: '',
    icon: LayoutDashboard,
    domain: 'stock',
    hidden: true,
    load: () => import('@/pages/stock/LegacyStockAnalysisRedirect'),
  },
];

/** Old addresses that now lead to a product (or home). */
export const PRODUCT_REDIRECTS: Array<{ from: string; to: string }> = [
  { from: '/trading-agents', to: '/products/stock-suite' },
  { from: '/openbb', to: '/products/stock-suite' },
  { from: '/go-stock', to: '/' },
  { from: '/career-ops', to: '/products/career-recommendations?tab=tracker' },
  {
    from: '/products/career-ops',
    to: '/products/career-recommendations?tab=tracker',
  },
  {
    from: '/proficiently',
    to: '/products/career-recommendations?tab=proficiently',
  },
  {
    from: '/products/proficiently',
    to: '/products/career-recommendations?tab=proficiently',
  },
  { from: '/marketplace-monitor', to: '/products/car-search?tab=marketplace' },
  {
    from: '/products/marketplace-monitor',
    to: '/products/car-search?tab=marketplace',
  },
];

function isVisible(product: ProductDefinition, ctx: ProductContext): boolean {
  if (!features.products) return false;
  if (product.service && !features[product.service]) return false;
  return product.isAvailable ? product.isAvailable(ctx) : true;
}

/** The redirects of every old address: the table's plus each product's own. */
export function allRedirects(
  products: readonly ProductDefinition[] = PRODUCTS
): Array<{ from: string; to: string }> {
  return [
    ...PRODUCT_REDIRECTS,
    ...products
      .filter((p) => p.legacyPath)
      .map((p) => ({ from: p.legacyPath!, to: p.path })),
  ];
}

export function visibleProducts(
  ctx: ProductContext,
  products: readonly ProductDefinition[] = PRODUCTS
): ProductDefinition[] {
  return products.filter((product) => isVisible(product, ctx));
}

export function findVisibleProduct(
  path: string,
  ctx: ProductContext,
  products: readonly ProductDefinition[] = PRODUCTS
): ProductDefinition | undefined {
  return visibleProducts(ctx, products).find((p) => p.path === path);
}
